<script lang="ts" setup>
import { parse, format } from 'date-fns'
import type { SeminarCartModule } from '~/store/seminarCart'
import { formatToccoPrice } from '~/lib/utils/toccoUtils'

const props = defineProps<{
  entity: SeminarCartModule
}>()

const { t } = useI18n()
const { removeModule } = useSeminarCartStore()

const formattedDate = computed(() =>
  props.entity.fieldStartDate?.value
    ? format(parse(props.entity.fieldStartDate.value, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')
    : props.entity.eLearningModule
      ? t('tocco.runSelection.eLearning')
      : '',
)

const formattedPrice = computed(() => formatToccoPrice(props.entity.fieldToccoData.paths.course_price?.value))

const removeProduct = () => {
  removeModule(props.entity)
}
</script>

<template>
  <div class="product-review-item">
    <div class="left">
      <p class="title">{{ entity.title }}</p>
      <div class="details">
        <p class="date-label">
          {{ t('tocco.registration.seminar.review.products.startDateLabel') }}
        </p>
        <p class="date">{{ formattedDate }}</p>
      </div>
    </div>
    <div class="middle">
      <p class="price">{{ formattedPrice }}</p>
    </div>
    <div class="right">
      <button class="remove" type="button" :aria-label="`${t('remove item')}: ${entity.title}`" @click="removeProduct">
        <Icon name="delete" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.product-review-item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  border-bottom: 1px solid rgba(24, 36, 111, 0.1);

  padding: 18px 0;

  grid-template-areas:
    'left right'
    '. middle';
  column-gap: 24px;

  @include breakpoint(tl) {
    grid-template-areas: 'left middle right';
    grid-template-columns: 1fr 210px auto;
  }

  .left {
    grid-area: left;

    .title {
      color: var(--c-text-dark);
      font-size: 20px;
      margin-bottom: 0;
      font-weight: 700;
    }

    .details {
      display: inline-flex;
      align-items: center;
      gap: 10px;

      .date-label,
      .date {
        font-size: 16px;
        color: var(--c-text-dark);

        margin-bottom: 0;
      }
    }
  }

  .middle {
    grid-area: middle;

    margin-left: auto;
    margin-right: 0;
    grid-column: span 2;

    @include breakpoint(tl) {
      grid-column: 1 / span 2;
    }

    .price {
      margin-bottom: 0;
    }
  }

  .right {
    grid-area: right;

    position: relative;
    display: flex;
    justify-content: center;
  }

  .remove {
    display: grid;
    place-items: center;

    width: 44px;
    height: 44px;
    padding: 0;

    color: var(--c-primary);
    background: transparent;
    border: 0;
    cursor: pointer;

    &:focus-visible {
      outline: 3px solid var(--c-blue-light-100);
      outline-offset: 2px;
    }
  }
}
</style>
